import React, { useMemo } from 'react';
import { Image, Platform, StyleSheet } from 'react-native';
import { SvgXml } from 'react-native-svg';
import { getIcon } from '@sodraberget/map-content/src/icon-library/iconLibrary.generated';

interface LibraryIconProps {
    iconKey?: string | null;
    color?: string;
    width?: number | string;
    height?: number | string;
    style?: any;
    /** Crop box in the 48x48 canvas. Defaults to the 32x32 safe area; use for non-square glyphs. */
    viewBox?: string;
}

// Library glyphs live inside a centred 32x32 safe area of a 48x48 canvas. In UI we crop to the
// safe area so the glyph fills the requested size like the legacy icon components did.
const SAFE_AREA_VIEWBOX = '8 8 32 32';

export const LibraryIcon = ({ iconKey, color, width, height, style, viewBox = SAFE_AREA_VIEWBOX }: LibraryIconProps) => {
    // Callers pass presentation via style (width/height/color), like the legacy icon components.
    const flatStyle = StyleSheet.flatten(style) ?? {};
    const resolvedColor = color ?? flatStyle.color ?? '#000';
    const resolvedWidth = width ?? flatStyle.width ?? 24;
    const resolvedHeight = height ?? flatStyle.height ?? 24;

    const xml = useMemo(() => {
        const icon = getIcon(iconKey);
        if (!icon) {
            return undefined;
        }
        const body = icon.body.replace(/currentColor/g, resolvedColor);
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" fill="${resolvedColor}">${body}</svg>`;
    }, [iconKey, resolvedColor, viewBox]);

    if (!xml) {
        return null;
    }
    if (Platform.OS === 'web') {
        // SvgXml from react-native-svg does not paint reliably on web; an Image with a data URI does.
        const uri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
        return <Image source={{ uri }} style={{ width: resolvedWidth, height: resolvedHeight }} />;
    }
    return <SvgXml xml={xml} width={resolvedWidth} height={resolvedHeight} style={style} />;
};
